import React from "react";
import { CountBadge } from "../../components/support/ChatPanel";
import {
  UsersIcon,
  CircleDollarSignIcon,
  SettingsIcon,
} from "../../components/icons";

// ── Icons not in the shared icons file ───────────────────────────────────────

const BuildingIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);

const SlidersIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="4" x2="4" y1="21" y2="14"/>
    <line x1="4" x2="4" y1="10" y2="3"/>
    <line x1="12" x2="12" y1="21" y2="12"/>
    <line x1="12" x2="12" y1="8"  y2="3"/>
    <line x1="20" x2="20" y1="21" y2="16"/>
    <line x1="20" x2="20" y1="12" y2="3"/>
    <line x1="2"  x2="6"  y1="14" y2="14"/>
    <line x1="10" x2="14" y1="8"  y2="8"/>
    <line x1="18" x2="22" y1="16" y2="16"/>
  </svg>
);

const UploadIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="17 8 12 3 7 8"/>
    <line x1="12" x2="12" y1="3" y2="15"/>
  </svg>
);

const ShieldIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    <polyline points="9 12 11 14 15 10"/>
  </svg>
);

const DevicesIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2"/>
    <line x1="8" y1="21" x2="16" y2="21"/>
    <line x1="12" y1="17" x2="12" y2="21"/>
  </svg>
);

const DownloadIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const ToothIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2C9 2 6 4 6 7c0 2 1 3.5 1.5 5.5C8 15 8.5 22 12 22s4-7 4.5-9.5C17 10.5 18 9 18 7c0-3-3-5-6-5z"/>
  </svg>
);

const SupportIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <path d="M12 16v-2a3 3 0 1 0-3-3"/>
    <line x1="12" y1="19.5" x2="12" y2="19.51"/>
  </svg>
);

// ── Nav items ─────────────────────────────────────────────────────────────────

interface NavItem {
  id:    string;
  label: string;
  icon:  React.ReactNode;
}

const NAV_ITEMS: NavItem[] = [
  { id: "profile",      label: "Profile & Account",         icon: <UsersIcon /> },
  { id: "devices",      label: "Trusted Devices",           icon: <DevicesIcon /> },
  { id: "clinic",       label: "Clinic Settings",           icon: <BuildingIcon /> },
  { id: "access",       label: "Access & Personalizations", icon: <SlidersIcon /> },
  { id: "finance",      label: "Finance & Billing",         icon: <CircleDollarSignIcon /> },
  { id: "privacy",      label: "Data & Privacy",            icon: <DownloadIcon /> },
  { id: "import",       label: "Data Import",               icon: <UploadIcon /> },
  { id: "compliance",   label: "Compliance & Verification", icon: <ShieldIcon /> },
  { id: "system",       label: "System & Preferences",      icon: <SettingsIcon /> },
  { id: "support",      label: "Support",                    icon: <SupportIcon /> },
  { id: "templates",    label: "Treatment Templates",        icon: <ToothIcon /> },
];

// ── Component ─────────────────────────────────────────────────────────────────

interface SettingsSidebarProps {
  active:      string;
  onSelect:    (id: string) => void;
  search:      string;
  /** Section ids that match the current search (label or any card). */
  highlighted: Set<string>;
  /**
   * Section ids this user may open, resolved by the parent from their
   * settings_<id>.view / .edit grants. `null` means "no restriction" (the
   * legacy-role fallback path when the backend hasn't sent grants).
   */
  allowedSections: Set<string> | null;
  /** Unread support-team replies, shown as a count on the Support item. */
  supportUnread?: number;
  /** Mobile off-canvas drawer state — desktop (lg:+) ignores these and stays always-visible. */
  isMobileOpen?:  boolean;
  onMobileClose?: () => void;
}

/** Wrap the portion of `label` matching `query` in a highlight mark. */
const highlightLabel = (label: string, query: string): React.ReactNode => {
  const q = query.trim();
  if (!q) return label;
  const idx = label.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return label;
  return (
    <>
      {label.slice(0, idx)}
      <mark className="bg-amber-200 text-gray-900 rounded px-0.5">
        {label.slice(idx, idx + q.length)}
      </mark>
      {label.slice(idx + q.length)}
    </>
  );
};

export const SettingsSidebar: React.FC<SettingsSidebarProps> = ({
  active,
  onSelect,
  search,
  highlighted,
  allowedSections,
  supportUnread = 0,
  isMobileOpen = false,
  onMobileClose,
}) => {
  const visibleItems = allowedSections
    ? NAV_ITEMS.filter(item => allowedSections.has(item.id))
    : NAV_ITEMS;

  const searching = search.trim().length > 0;

  const navList = (
    <nav className="flex flex-col gap-1 px-3">
      {visibleItems.map(item => {
        const isActive = item.id === active;
        const isMatch  = highlighted.has(item.id);
        // While searching, dim items that don't match so matches stand out.
        const dimmed = searching && !isMatch;
        return (
          <button
            key={item.id}
            onClick={() => onSelect(item.id)}
            className={`flex items-center gap-2.5 w-full rounded-lg px-3 min-h-[44px] text-left transition-all duration-200
              ${isActive
                ? "bg-gradient-to-r from-[#0e7d9c] to-[#3b82f6] text-white shadow-md shadow-blue-500/20"
                : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
              }
              ${searching && isMatch && !isActive ? "ring-1 ring-amber-400 bg-amber-50 text-gray-900" : ""}
              ${dimmed ? "opacity-40" : ""}`}
          >
            <span className={`flex-shrink-0 [&>svg]:w-[18px] [&>svg]:h-[18px]
              ${isActive ? "text-white" : "text-gray-400"}`}>
              {item.icon}
            </span>
            <span className="truncate text-xs font-medium">
              {highlightLabel(item.label, search)}
            </span>
            {item.id === "support" && <CountBadge count={supportUnread} className="ml-auto" />}
          </button>
        );
      })}
    </nav>
  );

  return (
    <>
      {/* Desktop — always-visible sidebar, unchanged from before */}
      <aside className="hidden lg:flex w-60 flex-shrink-0 flex-col py-4 overflow-y-auto
        bg-white border-r border-gray-200">
        {navList}
      </aside>

      {/* Mobile — off-canvas drawer, same interaction pattern as the main app Sidebar */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={onMobileClose}
        />
      )}
      <aside
        className={`fixed left-0 top-0 z-50 h-full w-full max-w-xs bg-white flex flex-col py-4 overflow-y-auto
          transition-transform duration-300 ease-in-out lg:hidden
          ${isMobileOpen ? "translate-x-0" : "-translate-x-full"}`}
        style={{ boxShadow: "4px 0 24px 0 rgba(0,0,0,0.10)" }}
      >
        <div className="flex items-center justify-between px-4 pb-3 mb-1 border-b border-gray-100">
          <span className="text-sm font-semibold text-gray-900">Settings</span>
          <button
            onClick={onMobileClose}
            className="p-1.5 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            aria-label="Close menu"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
            </svg>
          </button>
        </div>
        {navList}
      </aside>
    </>
  );
};
